import _ from 'lodash';
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import AvailableAppointmentTable from '@/visa-application/available-appointment-table/available-appointment-table.vue';
import AppointmentService from '@/visa-application/services/appointment/appointment.service';
import { IAvailableAppointmentModel } from '@/visa-application/models/AvailableAppointmentModel';
import TlsStoreService from '@/shared/config/service/tls-store.service';
import JhiAlertService from '@/shared/alert/alert.service';
import { ALL_COUNTRY_CODE, LOCAL_COUNTRY, ON_DEMAND } from '@/constants';
import DirectusService from '@/core/directus/directus.service';
import ApplicationCenterCMSModel from '@/visa-application/models/ApplicationCenterCMSModel';
import LanguageCode from '@/locale/language-code';
import CityCMSModel from '@/visa-application/models/CityCMSModel';
import SplashCMSModel from '@/visa-application/models/SplashCMSModel';
import CountryCMSModel from '@/visa-application/models/CountryCMSModel';
import CountryAvsCMSModel from '@/visa-application/models/CountryAvsCMSModel';
import ResidenceCountryCMSModel from '@/visa-application/models/ResidenceCountryCMSModel';
import { getCmsDataByCountryAndDefault, defineCanonicalTag } from '@/shared/utils/utils';
import CurrencyCMSModel from '@/visa-application/models/CurrencyCMSModel';
import { Keywords } from '../models/KeywordsCMSModel';

@Component({
  components: {
    'available-appointment-table': AvailableAppointmentTable
  }
})
export default class ApplierApplicationCenter extends Vue {
  public showPopup = false;
  public availableAppointments: IAvailableAppointmentModel[] = [];
  public residenceCountryCode: string;
  public applicationCenterCMSModels: ApplicationCenterCMSModel[] = [];
  public countryCMSData: CountryCMSModel;
  public countryAvsCMSData: CountryAvsCMSModel[] = [];
  public moreAvsInfoData: CountryAvsCMSModel = new CountryAvsCMSModel();
  public residenceCountryCMSData: ResidenceCountryCMSModel = new ResidenceCountryCMSModel();
  public onDemandCurrency: CurrencyCMSModel = new CurrencyCMSModel();
  public onDemand = ON_DEMAND;
  public keywords: Keywords;
  public splashCarousel: SplashCMSModel[] = [];
  public swiperOption = {};

  @Inject('tlsStoreService') private tlsStoreService: () => TlsStoreService;
  @Inject('appointmentService') private appointmentService: () => AppointmentService;
  @Inject('alertService') private alertService: () => JhiAlertService;
  @Inject('directusService') private directusService: () => DirectusService;
  private currentLanguage: LanguageCode = this.$store.getters.currentLanguage;
  public splashCMSData: SplashCMSModel = new SplashCMSModel();
  public cityCMSData: CityCMSModel = new CityCMSModel();
  public path = {};

  beforeRouteEnter(to, from, next) {
    defineCanonicalTag(to.params.residenceCountryCode, to.params.issuer, to.params.formGroupId, to.fullPath);

    next(vm => {
      if (to.params.residenceCountryCode) {
        vm.getCountryAndCenterFromCMS(to.params.residenceCountryCode);

        if (to.params.residenceCountryCode === 'ab') {
          vm.$store.commit('metaInfo', { ...vm.$store.getters.metaInfo, meta_robots: 'noindex, nofollow' });
        }
      }
    });
  }

  beforeRouteLeave(to, from, next) {
    next(vm => {
      if (from.params.residenceCountryCode === 'ab') {
        vm.$store.commit('metaInfo', { ...vm.$store.getters.metaInfo, meta_robots: '' });
      }
    });
  }

  @Watch('$store.getters.currentLanguage', { deep: true })
  public onLanguageChange(language: LanguageCode) {
    this.keywords = this.$store.getters.keywords;
    this.currentLanguage = language;
    this.getResidenceCountryContent();
    this.getApplicationCenterFromCMS();
    this.getCountryAvsFromCMS();
    this.retrieveNextAppointmentsAvailable(this.$route.params.residenceCountryCode);
    this.getSplashFromCMS();
    this.getCityNameTranslationForAppointmentTable();
  }

  @Watch('$store.getters.keywords', { immediate: true })
  public onKeywordsChanged(keywords: Keywords): void {
    this.keywords = keywords;
  }

  public async created(): Promise<void> {
    await this.retrieveNextAppointmentsAvailable(this.$route.params.residenceCountryCode);
    _.set(this.path, 'path', window.location.pathname);
  }

  public async retrieveNextAppointmentsAvailable(residenceCountryCode: string): Promise<void> {
    try {
      this.availableAppointments = await this.appointmentService().getNextAppointmentsAvailable(residenceCountryCode, LOCAL_COUNTRY);
    } catch (error) {
      this.alertService().showError(error);
    }
  }

  // get how many center in this country,if === 1 ,redirect to ApplicationCenterHome
  public getCountryAndCenterFromCMS(residenceCountryCode) {
    this.residenceCountryCode = residenceCountryCode;

    const countryCodeFilter = {
      code: {
        eq: this.residenceCountryCode
      }
    };
    const centerFilter = {
      'country.code': {
        eq: this.residenceCountryCode
      }
    };
    this.directusService()
      .getCollection<CountryCMSModel>(CountryCMSModel, 'country', ['name', 'code'], countryCodeFilter, this.currentLanguage)
      .then((response: CountryCMSModel[]) => {
        this.countryCMSData = response[0];
        if (response[0].code) {
          // if country exist ,get center
          this.directusService()
            .getCollection<ApplicationCenterCMSModel>(
              ApplicationCenterCMSModel,
              'application_centers',
              ['code'],
              centerFilter,
              this.currentLanguage
            )
            .then(async (result: ApplicationCenterCMSModel[]) => {
              // if only one center,redirect to ApplicationCenterHome
              if (result.length === 1) {
                this.$router.push({
                  name: 'ApplicationCenterHome',
                  params: {
                    residenceCountryCode: this.residenceCountryCode,
                    issuer: result[0].code
                  }
                });
              }
              await this.getApplicationCenterFromCMS();
              this.getResidenceCountryContent();
              this.getCountryAvsFromCMS();
              this.retrieveNextAppointmentsAvailable(this.$route.params.residenceCountryCode);
              this.getSplashFromCMS();
              this.getCityNameTranslationForAppointmentTable();
            })
            .catch(error => console.error(error));
        }
      })
      .catch(error => console.error(error));
  }

  public async getApplicationCenterFromCMS(): Promise<void> {
    const centerFilter = {
      'country.code': {
        eq: this.residenceCountryCode
      }
    };
    await this.directusService()
      .getCollection<ApplicationCenterCMSModel>(
        ApplicationCenterCMSModel,
        'application_centers',
        ['*.*'],
        centerFilter,
        this.currentLanguage
      )
      .then((result: ApplicationCenterCMSModel[]) => {
        result.forEach(center => {
          if (center.name_translation === '' || center.name_translation === null) {
            center.name_translation = center.name;
          }
        });
        this.applicationCenterCMSModels = _.sortBy(result, 'name_translation');
      })
      .catch(error => console.error(error));
  }

  public chooseApplicationCenter(residenceCountry: string, applicationCenter: ApplicationCenterCMSModel) {
    this.tlsStoreService().setIssuer(applicationCenter.code);
  }

  public getSplashFromCMS(): void {
    const splashFilter = {
      'country.code': {
        in: [this.residenceCountryCode, ALL_COUNTRY_CODE]
      }
    };
    this.directusService()
      .getCollection<SplashCMSModel>(SplashCMSModel, 'splash', ['*.*'], splashFilter, this.currentLanguage)
      .then((response: SplashCMSModel[]) => {
        // Store the data wich has the country code in the response
        const splashCmsData = _.find(response, ['country.code', this.residenceCountryCode]);
        // Store the data wich has the WW code in the response
        const defaultCountryContent = _.find(response, ['country.code', ALL_COUNTRY_CODE]);
        /**
         * Map only if it has the country code response
         * if it's and the element is not defined
         * replace the correspond element for the default element in the WW response
         */
        _.map(splashCmsData, (element, index) => {
          if (!element) {
            _.set(splashCmsData, index, _.get(defaultCountryContent, index));
          }
        });
        // Store the response that isn't undefined
        this.splashCarousel = splashCmsData || defaultCountryContent;
        this.splashCMSData = getCmsDataByCountryAndDefault(response, this.residenceCountryCode);
        this.$store.commit('metaInfo', { ...this.$store.getters.metaInfo, ...this.splashCMSData });
        // 设置轮播时间
        // Set the rotation time
        if (Number(this.splashCarousel['swipeAutoplayTime'])) {
          this.swiperOption = {
            slidesPerView: 'auto',
            loop: true,
            speed: 1,
            observer: true,
            observeParents: true,
            autoplay: {
              delay: Number(this.splashCarousel['swipeAutoplayTime']) * 1000,
              disableOnInteraction: false
            }
          };
        } else {
          this.swiperOption = {
            slidesPerView: 'auto',
            loop: true,
            speed: 1,
            observer: true,
            observeParents: true,
            autoplay: {
              delay: 10000,
              disableOnInteraction: false
            }
          };
        }
      })
      .catch(error => console.error(error));
  }

  public getCountryAvsFromCMS() {
    const countryAvsFilter = {
      'country.code': {
        eq: this.residenceCountryCode
      },
      'country.status': {
        eq: 'published'
      }
    };
    this.directusService()
      .getCollection<CountryAvsCMSModel>(
        CountryAvsCMSModel,
        'country_avs',
        ['*', 'translation.*', 'avs.*', 'avs.image.data', 'avs.translation.*', 'currency.code', 'currency.translation.*'],
        countryAvsFilter,
        this.currentLanguage
      )
      .then((result: CountryAvsCMSModel[]) => {
        this.countryAvsCMSData = _.sortBy(result, 'weight');
        if (this.countryAvsCMSData.length > 3) {
          this.countryAvsCMSData = this.countryAvsCMSData.slice(0, 3);
        }
      })
      .catch(error => console.error(error));

    this.directusService()
      .getCollection<CurrencyCMSModel>(CurrencyCMSModel, 'currencies', ['*.*'], undefined, this.currentLanguage)
      .then((res: CurrencyCMSModel[]) => {
        this.onDemandCurrency = res.find(currency => currency.code === this.onDemand);
      })
      .catch(error => console.error(error));
  }

  public getCityNameTranslationForAppointmentTable(): void {
    const cityFilter = {
      'country.code': {
        eq: this.residenceCountryCode
      }
    };

    this.directusService()
      .getCollection<CityCMSModel>(CityCMSModel, 'city', ['translation.city_name_translation'], cityFilter, this.currentLanguage)
      .then((response: CityCMSModel[]) => {
        this.cityCMSData = response[0];
        if (this.cityCMSData) {
          _.forEach(this.availableAppointments, availableAppointment => {
            _.forEach(this.cityCMSData.city_name_translation, cityNameTranslation => {
              if (availableAppointment.city.toLowerCase() === cityNameTranslation.name_en.toLowerCase()) {
                availableAppointment.city = cityNameTranslation.name_translation;
              }
            });
          });
        }
        this.availableAppointments.sort();
      })
      .catch(error => console.error(error));
  }

  public onAVSMoreInfoClick(countryAvs: CountryAvsCMSModel): void {
    this.moreAvsInfoData = countryAvs;
    this.showPopup = true;
  }

  public closeAvsPopup(): void {
    this.showPopup = false;
  }

  private getResidenceCountryContent() {
    this.directusService()
      .getCollection<ResidenceCountryCMSModel>(
        ResidenceCountryCMSModel,
        'residence_country',
        ['country_flag.*', 'translation.welcome_header', 'translation.welcome_content', 'translation.splash_header'],
        undefined,
        this.currentLanguage
      )
      .then((response: ResidenceCountryCMSModel[]) => {
        this.residenceCountryCMSData = response[0];
        Object.keys(this.residenceCountryCMSData).forEach(key => {
          if (
            this.residenceCountryCMSData[key] &&
            typeof this.residenceCountryCMSData[key] === 'string' &&
            this.applicationCenterCMSModels.length > 0
          ) {
            this.directusService()
              .replaceDetailCodes(this.residenceCountryCMSData[key], this.applicationCenterCMSModels[0].code, this.currentLanguage)
              .then(result => {
                this.residenceCountryCMSData[key] = result;
              });
          }
        });
      })
      .catch(error => console.error(error));
  }
}
